/* eslint-disable camelcase */
import { Alert, AlertBody, AlertIcon, AlertProps, AlertTitle, Stack } from 'component-library';

import { DownloableFileInfo } from '@/components/DownloableFileInfo';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { isNullish } from '@/utils/booleans';

export const DISCLAIMER_DOMAIN = {
  OVERVIEW: 'overview',
  NATURAL_CAPITAL_ACCOUNT: 'natural_capital_account',
  LAND_MONITORING: 'land_monitoring',
} as const;

export type DisclaimerDomain = (typeof DISCLAIMER_DOMAIN)[keyof typeof DISCLAIMER_DOMAIN];

type DisclaimerFactValue = {
  domain: DisclaimerDomain;
  content?: DisclaimerContent;
};

type DisclaimerContent = {
  type?: AlertProps['type'];
  title?: string;
  description?: string;
  icon?: string;
  file_info_ids?: string[];
};

export const Disclaimer = ({ domain }: { domain: DisclaimerDomain }) => {
  const fact = useFact<DisclaimerFactValue | DisclaimerFactValue[], 'conservation_disclaimer'>(
    'conservation_disclaimer',
  );

  const values = Array.isArray(fact?.value) ? (fact.value as DisclaimerFactValue[]) : [];
  const content = values.find((value) => value.domain === domain)?.content;

  if (isNullish(content)) {
    return null;
  }

  const { type = 'default', title, description, icon, file_info_ids } = content;
  const fileInfoIds = Array.isArray(file_info_ids) ? file_info_ids : [];

  if (isNullish(title) && isNullish(description) && fileInfoIds.length === 0) {
    return null;
  }

  return (
    <Alert type={type} data-testid='conservation-disclaimer'>
      {icon && (
        <AlertIcon>
          <i className={icon} />
        </AlertIcon>
      )}
      <Stack spacing={2}>
        {title && <AlertTitle>{title}</AlertTitle>}
        {description && <AlertBody>{description}</AlertBody>}
        {fileInfoIds.length > 0 && (
          <Stack spacing={4} className='w-fit'>
            {fileInfoIds.map((fileInfoId) => (
              <DownloableFileInfo key={fileInfoId} fileInfoId={fileInfoId} />
            ))}
          </Stack>
        )}
      </Stack>
    </Alert>
  );
};
